import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import SearchInput from "../../../utils/SearchInput";
import defaultImage from "../../../utils/DefaultImage";
import TableLoader from "../../../utils/TableLoader";
import PaginationComponent from "../../../utils/PaginationComponent";
import { FaPlus } from "react-icons/fa";
import { TbEdit } from "react-icons/tb";
import { BiSolidTrashAlt } from "react-icons/bi";
import { motion, AnimatePresence } from "framer-motion";
import {
  containerVariants,
  itemVariants,
  cardVariants,
  buttonAnimations,
  dialogVariants,
} from "../../../utils/Animations";
import DishAdd from "./DishAdd";
import DishEdit from "./DishEdit";

const DishManagement = ((props, ref) => {

  const { rest_slug2 } = useParams();
  const navigate = useNavigate();
  const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
  const logoUrl = import.meta.env.VITE_BACKEND_APP_STORAGE_URL;
  const token = localStorage.getItem("token");

  const [isLoading, setIsLoading] = useState({ main: false, edit: false });
  const [dishesData, setDishesData] = useState({
    name: "",
    description: "",
  });

  const [logo, setLogo] = useState(null);
  const [selecteLogo, setSelecteLogo] = useState('');
  const [logoError, setLogoError] = useState('');

  const [dishListData, setDishListData] = useState([]);
  const [currentDishData, setCurrentDishData] = useState(null);
  const [isAdd, setIsAdd] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [selectedDishId, setSelectedDishId] = useState(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [showOption, setShowOption] = useState({ allergens: false, course: false });
  const [selectedCourse, setSelectedCourse] = useState([]);
  const [courseData, setCourseData] = useState([]);
  const [allergensData, setAllergensData] = useState([]);
  const [selectedAllergens, setSelectedAllergens] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState({ allergens: "", course: "" });

  const handleSearchChange = (value) => {
    setSearchText(value);
  };

  const fetchCourseData = async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/v1/restaurant/${rest_slug2}/dish-courses`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: searchQuery.course, pagination: 0 },
      });

      setCourseData(response?.data?.data);
    } catch (error) {
      console.error("Error fetching allergens", error);
    }
  };

  const fetchAllergens = async () => {
    try {
      const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
      const token = localStorage.getItem("token");

      const response = await axios.get(`${apiUrl}/api/v1/get-allergens`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: searchQuery.allergens, pagination: 0 },
      });

      setAllergensData(response?.data?.data);
    } catch (error) {
      console.error("Error fetching allergens", error);
    }
  };

  const toggleDropdown = (key) => {
    setShowOption((prev) => ({
      allergens: key === "allergens" ? !prev.allergens : false,
      course: key === "course" ? !prev.course : false,
    }));
  };

  const handleCourseSelection = (courseId) => {
    setSelectedCourse(courseId);
    setTimeout(() => {
      toggleDropdown("course");
    }, 100);
  };

  const handleAllergenSelection = (allergenId) => {
    setSelectedAllergens((prev) =>
      prev.includes(allergenId) ? prev.filter((id) => id !== allergenId) : [...prev, allergenId]
    );
  };

  const getDishesData = async () => {
    setIsLoading({ ...isLoading, main: true });
    try {
      let params;
      if (searchText) {
        params = {
          pageNumber: page,
          size: rowsPerPage,
          search: searchText,
        };
      } else {
        params = {
          pageNumber: page,
          size: rowsPerPage,
        };
      }
      const response = await axios.get(`${apiUrl}/api/v1/restaurants/${rest_slug2}/dishes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        params,
      });

      const data = response?.data;
      setDishListData(data?.data);
      setTotalPages(data?.total);
    } catch (error) {
      console.error('Error fetching order data:', error);
    } finally {
      setIsLoading({ ...isLoading, main: false });
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const maxSizeInBytes = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSizeInBytes) {
        setLogoError('Max size: 2MB. Please upload a smaller file.');
        return;
      }

      const img = new Image();
      img.onload = () => {
        setLogoError('');
        const fileUrl = URL.createObjectURL(file);
        setLogo(fileUrl);
        setSelecteLogo(file);
      };

      img.onerror = () => {
        setLogoError('Invalid image file. Please select a valid image.');
      };

      img.src = URL.createObjectURL(file);
    } else {
      setLogoError('No file selected. Please choose a file to upload.');
    }
  };

  const handleSave = async () => {
    if (logoError) {
      toast.error(logoError, { theme: "colored", autoClose: 1000 });
      return;
    }
    setIsLoading({ ...isLoading, edit: true });
    try {
      if (!token) {
        console.error('No token found in localStorage.');
        return;
      }
      const formData = new FormData();
      formData.append("name", dishesData.name);
      if (selectedAllergens.length > 0) {
        selectedAllergens.forEach((allergenId) =>
          formData.append("allergens[]", allergenId)
        );
      } else {
        formData.append("allergens", "");
      }
      formData.append("description", dishesData.description);
      if (selectedCourse) {
        formData.append("dish_course_id", selectedCourse);
      } else {
        formData.append("dish_course_id", "");
      }
      if (selecteLogo) {
        formData.append("picture", selecteLogo);
      } else if (logo === null) {
        formData.append("picture", "");
      }
      const response = await axios.post(
        `${apiUrl}/api/v1/dishes/${currentDishData.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      getDishesData();
      toast.success(response?.data?.message || "Dish Updated Successfully!", {
        theme: "colored",
        autoClose: 1000,
      });
      setIsEditing(false);
      navigate(
        `/restaurantPanel/dishManagement/${localStorage.getItem("currentRestId") &&
        localStorage.getItem("currentRestId")
        }`
      );
    } catch (error) {
      console.error("Error adding dish:", error?.response?.data?.errors);
      const errors = error?.response?.data?.errors;
      if (errors) {
        for (const key in errors) {
          if (Array.isArray(errors[key])) {
            errors[key].forEach((message) => {
              toast.error(message, {
                theme: "colored",
                autoClose: 3000,
              });
            });
          }
        }
      } else {
        toast.error(error?.response?.data?.message, {
          theme: "colored",
          autoClose: 3000,
        });
      }
    } finally {
      setIsLoading({ ...isLoading, edit: false });
    }
  };

  const handleDelete = async (id) => {
    if (!id) {
      console.error('Error: ID is required to delete a Dish.');
      return;
    }
    setIsLoading({ ...isLoading, main: true });
    try {
      const response = await axios.delete(`${apiUrl}/api/v1/dishes/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      getDishesData();
      toast.success(response?.data?.message || 'Dish deleted successfully:', { theme: "colored", autoClose: 1000 });
    } catch (error) {
      console.error('Error deleting dish:', error?.response?.data?.message || error.message);
      toast.error('Failed to delete the dish. Please try again.', { theme: 'colored', autoClose: 3000 });
    } finally {
      setIsLoading({ ...isLoading, main: false });
    }
  };

  const handleEditClick = (data) => {
    setIsEditing(true);
    setCurrentDishData(data);
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsAdd(false);
    setCurrentDishData(null);
  };

  const deleteProfile = () => {
    setSelecteLogo('');
    setLogo(null);
  };

  useImperativeHandle(ref, () => ({
    dishManagement() {
      setIsEditing(false);
      setIsAdd(false);
      setCurrentDishData(null);
    },
  }));

  useEffect(() => {
    setSearchQuery({ allergens: "", course: "" });
  }, [showOption]);

  useEffect(() => {
    fetchCourseData();
  }, [searchQuery.course]);

  useEffect(() => {
    fetchAllergens();
  }, [searchQuery.allergens]);

  useEffect(() => {
    if (
      currentDishData?.name ||
      currentDishData?.picture ||
      currentDishData?.allergens ||
      currentDishData?.description
    ) {
      setDishesData({
        name: currentDishData?.name || "",
        description: currentDishData?.description || "",
      });

      const allergenIds = currentDishData?.allergens?.map((allergen) => allergen.id) || [];
      setSelectedAllergens(allergenIds);

      if (currentDishData?.dish_courses?.length > 0) {
        setSelectedCourse(currentDishData?.dish_courses[0]?.id);
      }

      const completeLogoUrl = currentDishData?.picture
        ? `${logoUrl}/${currentDishData?.picture}`
        : null;
      setLogo(completeLogoUrl);
    }
  }, [currentDishData]);

  useEffect(() => {
    getDishesData();
  }, [page, rowsPerPage, searchText]);

  useEffect(() => {
    const handlePageRefresh = () => {
      localStorage.removeItem("searchTextUser");
    };
    window.addEventListener("beforeunload", handlePageRefresh);
    return () => {
      window.removeEventListener("beforeunload", handlePageRefresh);
    };
  }, []);

  return (
    <>
      <AnimatePresence mode="wait">
        {isAdd ? (
          <motion.div
            key="add-dish"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <DishAdd
              setIsAdd={setIsAdd}
              getDishesData={getDishesData}
            />
          </motion.div>
        ) : isEditing ? (
          <motion.div
            key="edit-dish"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <DishEdit
              logo={logo}
              setLogo={setLogo}
              logoError={logoError}
              setLogoError={setLogoError}
              handleLogoChange={handleLogoChange}
              dishesData={dishesData}
              setDishesData={setDishesData}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              deleteProfile={deleteProfile}

              showOption={showOption}
              setShowOption={setShowOption}
              toggleDropdown={toggleDropdown}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              courseData={courseData}
              selectedCourse={selectedCourse}
              handleCourseSelection={handleCourseSelection}
              allergensData={allergensData}
              selectedAllergens={selectedAllergens}
              handleAllergenSelection={handleAllergenSelection}
              handleSave={handleSave}
              handleCancel={handleCancel}
            />
          </motion.div>
        ) : (
          <motion.div
            key="dish-list"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="space-y-6"
          >
            <motion.section
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="border-t border-[#D9D9D9] sm:mt-5 pt-5"
            >
              <motion.div
                className="flex lg:flex-row flex-col justify-between items-center mt-3 mb-3 w-full"
                variants={containerVariants}
              >
                <motion.h3
                  className="text-3xl font-bold mb-2 poppins-medium self-start"
                  variants={itemVariants}
                >
                  Gestisci Piatti
                </motion.h3>
                <motion.div
                  className="self-start flex items-center gap-4 sm:flex-row flex-col sm:w-auto w-full"
                  variants={itemVariants}
                >
                  <motion.div variants={itemVariants}>
                    <SearchInput
                      onSearchChange={handleSearchChange}
                      component="Dish Management"
                      placeholder="Piatti"
                    />
                  </motion.div>
                  <motion.button
                    className="flex sm:w-auto w-full items-center justify-center px-2 py-1 bg-[#8A2533] text-white rounded-lg border-2 border-[#953543] shadow-sm hover:bg-[#8A2533] transition-all duration-200 group"
                    onClick={() => setIsAdd(true)}
                    {...buttonAnimations}
                  >
                    <FaPlus />
                    <span className="ml-2 poppins-regular">
                      Aggiungi Piatto
                    </span>
                  </motion.button>
                </motion.div>
              </motion.div>
              <motion.div
                className="user-mangement-main w-auto h-auto bg-[#FBFBFB] border-[2px] border-[#8B24332B] px-6 pt-3 pb-0 rounded-xl shadow"
                variants={cardVariants}
              >
                <table className="w-full text-left wine_table">
                  <thead>
                    <motion.tr
                      className="border-b border-[#8B2433]"
                      variants={itemVariants}
                    >
                      <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Immagine</th>
                      <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Nome del piatto</th>
                      <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Descrizione</th>
                      <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Allergeni</th>
                      <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Portata</th>
                      <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Opzioni</th>
                    </motion.tr>
                  </thead>
                  {isLoading.main ? (
                    <TableLoader />
                  ) : (
                    <tbody>
                      {dishListData && dishListData?.length > 0 ? (
                        dishListData?.map((dish, index) => (
                          <motion.tr
                            key={index}
                            className="border-b border-[#8B2433]/12"
                            variants={itemVariants}
                            custom={index}
                          >
                            <td className="pr-2 py-4">
                              <motion.div
                                className="thumbnail_image rounded-lg w-[50px] h-[60px] p-1 flex align-center justify-center"
                                {...buttonAnimations}
                              >
                                <img
                                  className="w-10 h-10 rounded-full"
                                  src={
                                    dish?.picture && dish?.picture !== ""
                                      ? `${logoUrl}/${dish?.picture}`
                                      : defaultImage
                                  }
                                  alt={dish?.name}
                                />
                              </motion.div>
                            </td>
                            <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                              {dish?.name?.replace(/\s+/g, ' ') || "N/A"}
                            </td>
                            <td className="py-4 px-2 text-left poppins-medium text-[#303030] max-w-[200px] truncate">
                              {dish?.description?.replace(/\s+/g, ' ') || "-"}
                            </td>
                            <td className="py-4 px-2 text-left poppins-medium text-[#303030] relative group cursor-pointer">
                              {dish?.allergens?.length > 0 ? (
                                <motion.div
                                  className="relative group inline-block"
                                  variants={itemVariants}
                                >
                                  <span>{dish.allergens[0].name_it}</span>
                                  {dish.allergens.length > 1 && (
                                    <motion.div
                                      className="absolute tool-tips-hover bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-sm rounded px-2 py-1"
                                      variants={itemVariants}
                                    >
                                      {dish.allergens.slice(1).map((allergen, idx) => (
                                        <span key={idx}>
                                          {allergen.name_it}
                                          {idx < dish.allergens.length - 2 && ', '}
                                        </span>
                                      ))}
                                    </motion.div>
                                  )}
                                </motion.div>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                            <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                              {dish?.dish_courses?.length > 0 ? (
                                <motion.div
                                  className="relative group inline-block"
                                  variants={itemVariants}
                                >
                                  <span>{dish.dish_courses[0].name}</span>
                                  {dish.dish_courses.length > 1 && (
                                    <motion.div
                                      className="absolute tool-tips-hover bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-sm rounded px-2 py-1"
                                      variants={itemVariants}
                                    >
                                      {dish.dish_courses.slice(1).map((dish_courses, idx) => (
                                        <span key={idx}>
                                          {dish_courses.name}
                                          {idx < dish.dish_courses.length - 2 && ', '}
                                        </span>
                                      ))}
                                    </motion.div>
                                  )}
                                </motion.div>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                            <td className="py-4 px-2 w-[100px]">
                              <motion.button
                                className="text-red-700 mx-2"
                                onClick={() => handleEditClick(dish)}
                                {...buttonAnimations}
                              >
                                <TbEdit size={"20px"} />
                              </motion.button>
                              <motion.button
                                className="text-red-700"
                                onClick={() => { setDialog(true); setSelectedDishId(dish?.id); }}
                                {...buttonAnimations}
                              >
                                <BiSolidTrashAlt size={"18px"} />
                              </motion.button>
                              <AnimatePresence>
                                {dialog && (
                                  <motion.div
                                    className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-20 z-50"
                                    variants={dialogVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                  >
                                    <motion.div
                                      className="bg-white rounded-lg shadow-lg sm:w-1/3 w-[280px]"
                                      variants={dialogVariants}
                                    >
                                      <div className="px-6 py-4 border-b">
                                        <h3 className="text-lg font-semibold text-gray-800 poppins-medium">
                                          Are you sure you want to delete the dish?
                                        </h3>
                                      </div>
                                      <div className="px-6 py-4 flex justify-end space-x-4">
                                        <motion.button
                                          onClick={() => { handleDelete(selectedDishId); setDialog(false); }}
                                          className="px-4 py-2 poppins-regular bg-[#8A2533] text-white rounded-md hover:bg-[#f6efef] hover:text-[#8A2533] transition-colors"
                                          {...buttonAnimations}
                                        >
                                          Yes
                                        </motion.button>
                                        <motion.button
                                          onClick={() => setDialog(false)}
                                          className="px-4 py-2 poppins-regular bg-gray-300 text-gray-700 rounded-md hover:text-white hover:bg-[#8A2533] transition-colors"
                                          {...buttonAnimations}
                                        >
                                          No
                                        </motion.button>
                                      </div>
                                    </motion.div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </td>
                          </motion.tr>
                        ))
                      ) : (
                        <motion.tr variants={itemVariants}>
                          <td colSpan={9} className="text-center py-4">No Dishes found</td>
                        </motion.tr>
                      )}
                    </tbody>
                  )}
                </table>
              </motion.div>
              {dishListData && dishListData?.length > 0 && (
                <motion.div variants={itemVariants}>
                  <PaginationComponent
                    page={page}
                    totalPages={totalPages}
                    rowsPerPage={rowsPerPage}
                    setPage={setPage}
                    setRowsPerPage={setRowsPerPage}
                  />
                </motion.div>
              )}
            </motion.section>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});

export default forwardRef(DishManagement);